/**
 * THE FLOW EDITOR — draw what the phone says.
 *
 * WHAT THIS SCREEN IS. `playbooks.graph` is what this canvas draws. `playbooks.steps` is what
 * answers the phone. Saving compiles the drawing into steps and runs the validator that has
 * always guarded the runtime; nothing compiles the other way. So the worst a broken drawing
 * can do is be refused — which is the entire reason the canvas is allowed to be ambitious.
 *
 * THE FOUR THINGS THAT MAKE IT MORE THAN A GRAPH TOY, in the order they matter:
 *
 *  1. THE CARDS SHOW THE WORDS. A "say" node reads as the sentence the caller will hear. An
 *     owner should be able to read the canvas aloud and recognise their own phone line. Nobody
 *     buys a picture of a state machine.
 *  2. THE READ-THROUGH. Beside the canvas, the same flow as an ordered list of sentences.
 *     It is the plain-language truth, it updates as you draw, and it is what makes this screen
 *     usable by somebody who cannot use a drag-and-drop graph at all.
 *  3. ERRORS LAND ON THE CARD. The compiler that runs on save also runs here, in the browser,
 *     on every keystroke — it is the same module (`src/api/flow-compile.ts`), not a second
 *     implementation that drifts. So the message you get before you press Save is the message
 *     the server would have given you, attached to the step that caused it.
 *  4. IT REFUSES TO DRAW AN IMPOSSIBLE SHAPE. One wire per outlet, because the compiler
 *     follows one. Deleting a step heals the wire through it. A branch outcome cannot be
 *     blank, because the outcome IS the case value.
 *
 * GRAPH_STALE IS THE SAFETY RAIL AND IT IS NOT SUBTLE. A flow whose steps were written by
 * something other than a compile of this graph — a shipped template, a hand edit — opens
 * LOCKED, with what it currently says on the phone shown in full and a veil over the canvas.
 * Drawing an empty canvas over a working flow and inviting somebody to press Save is how a
 * call flow disappears at 2 AM.
 *
 * DOCTRINE, VISIBLE RATHER THAN ASSUMED. There is no node that bridges a caller to a person
 * and the `page` card says so on its face (1). The `do` node is orchid and says "proposed for
 * approval" (2). Every control that writes is behind `policy.write`; a viewer gets the canvas,
 * the read-through and no Save button at all (6).
 *
 * ── WHY THIS SCREEN IS NOT A KIOSK, AND WHAT THAT COST ──────────────────────────────────
 *
 * Every other screen in this product is now tiles, sheets and steppers. This one is not, and
 * the refusal is the design decision, not an omission. A call flow is a directed graph: it
 * branches, it rejoins, and the SHAPE is the information. Rendered as a stack of 104px tiles
 * you would have a list that cannot show a branch — the same mistake as the fighter-pilot
 * console, made in the opposite direction. The canvas stays a canvas.
 *
 * What DID move: the chrome around it. Kiosk face and kiosk scale throughout (flow.css), the
 * header buttons are BigButtons, the settings dialog is a Sheet with a kiosk field and two
 * switch tiles where a <select> used to be, and every pressable thing in the chrome — back,
 * undo, redo, zoom, fit, a palette row, a starter row — clears 56px. The read-through is set
 * at 16px, because it is the half of this screen usable by somebody who cannot drag at all.
 *
 * The node cards keep their 13px body on purpose: READABLE_ZOOM and the fit maths at the
 * bottom of this file are measured against a 264×210 card, and growing one without the other
 * puts the first paint back at seven-pixel type.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Background,
  BackgroundVariant,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import type { Connection, EdgeChange, IsValidConnection, NodeChange, NodeTypes } from '@xyflow/react';
import type { ReactElement } from 'react';

import '@xyflow/react/dist/style.css';
import '../styles/kiosk-opensky.css';
import '../styles/flow.css';

import { api, flowTriggerLabel } from '../lib/api.ts';
import type { FlowDetail, FlowGraph as WireGraph, FlowKind } from '../lib/api.ts';
import { useCan, useResource } from '../lib/useApi.ts';
import {
  Badge,
  BigButton,
  Callout,
  ErrorState,
  HelpTip,
  Icon,
  LinkAction,
  RelativeTime,
  Sheet,
  SkeletonCard,
  SwitchTile,
  Switches,
  useToast,
} from '../components/index.ts';
import { FlowUiContext } from '../components/flow/FlowUiContext.tsx';
import { Inspector } from '../components/flow/Inspector.tsx';
import type { SituationOption } from '../components/flow/Inspector.tsx';
import { DRAG_MIME, Palette } from '../components/flow/Palette.tsx';
import { StarterList } from '../components/flow/StarterList.tsx';
import { ReadThrough } from '../components/flow/ReadThrough.tsx';
import { ScriptView } from '../components/flow/ScriptView.tsx';
import { NODE_COMPONENTS } from '../components/flow/nodes.tsx';
import { FALLBACK_SITUATIONS, NODE_SPECS, toolLabel } from '../components/flow/model.ts';
import type { NdKind } from '../components/flow/model.ts';
import {
  EMPTY_GRAPH,
  addCase,
  appendAfter,
  casesOf,
  fromWire,
  hasFreeOutlet,
  insertOnEdge,
  makeNode,
  nodeById,
  outgoing,
  patchData,
  removeCase,
  removeNodes,
  renameCase,
  sameGraph,
  seedGraph,
  tidy,
  toWire,
  connect as connectEdge,
} from '../components/flow/graph.ts';
import type { NdGraph, NdNode } from '../components/flow/graph.ts';
import { compilePreview, flowLevelIssues, issuesByNode } from '../components/flow/readthrough.ts';
import type { CompileIssue } from '../components/flow/readthrough.ts';
import { fetchSituations, saveFlowGraph } from '../components/flow/flowIo.ts';
import { useFlowHistory } from '../components/flow/useFlowHistory.ts';
import type { PlaybookStep } from '../../../src/api/contract.ts';

/**
 * React Flow types `nodeTypes` as a map of components taking the base `NodeProps`, and our
 * cards take the narrowed `NodeProps<NdNode>`. The narrowing is the point — it is what stops
 * a card reading `data.mesage` — so the assertion is here, once, rather than every card
 * widening its own props back to `unknown`.
 */
const NODE_TYPES = NODE_COMPONENTS as unknown as NodeTypes;

const DEFAULT_EDGE = {
  type: 'smoothstep',
  markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
} as const;

export default function FlowEditorScreen(): ReactElement {
  return (
    <ReactFlowProvider>
      <FlowEditor />
    </ReactFlowProvider>
  );
}

function FlowEditor(): ReactElement {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canWrite = useCan('policy.write');
  const rf = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  /**
   * `/api/flows/:id` answers `{ item, palette }`. The palette is the list screen's business;
   * this screen keeps the flow itself in the cache so a save can drop the new one straight in
   * without re-deriving the envelope.
   */
  const query = useResource<FlowDetail>(['flow', id], () => api.flows.get(id).then((r) => r.item), {
    enabled: Boolean(id),
  });
  const detail = query.data;

  // ------------------------------------------------------------------ the drawing itself
  const history = useFlowHistory(EMPTY_GRAPH);
  const { graph, apply, commit } = history;
  const [savedGraph, setSavedGraph] = useState<NdGraph>(EMPTY_GRAPH);
  /** Which starter is currently drawn, so the list can show it as the one in view. */
  const [starterCode, setStarterCode] = useState<string | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  /**
   * SCRIPT is the front door; DIAGRAM is the toggle. Most people who own a phone line want to
   * read what it says and change a sentence, not draw a graph — so the script leads, and the
   * canvas is one press away for the shape. A template-built flow that used to open as a
   * locked empty canvas now opens as its own script, read-only until you choose to redraw it.
   */
  const [view, setView] = useState<'script' | 'diagram'>('script');

  /**
   * A flow whose steps were not compiled from this graph opens locked. `acknowledged` is the
   * operator saying, in as many words, "yes, replace it" — and until they do, nothing on this
   * screen can write.
   */
  const [acknowledgedStale, setAcknowledgedStale] = useState(false);
  const staleLocked = Boolean(detail?.graphStale) && !acknowledgedStale;
  const readOnly = !canWrite || staleLocked;

  /** Nothing drawn yet beyond the entry step the server creates. */
  const isBlank = graph.nodes.length <= 1;

  useEffect(() => {
    if (!detail || loadedId === detail.id) return;
    const loaded = fromWire(detail.graph);
    const opening = loaded.nodes.length > 0 ? loaded : detail.graphStale ? EMPTY_GRAPH : seedGraph();
    history.reset(opening);
    setSavedGraph(opening);
    setLoadedId(detail.id);
    setAcknowledgedStale(false);
    // history is a stable object of stable callbacks; depending on it would reload the
    // canvas on every keystroke and throw away what the operator is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, loadedId]);

  // ------------------------------------------------------------------ situations for `arm`
  const [situations, setSituations] = useState<SituationOption[]>(FALLBACK_SITUATIONS);
  useEffect(() => {
    let live = true;
    void fetchSituations().then((list) => {
      if (live) setSituations(list);
    });
    return () => {
      live = false;
    };
  }, []);

  // ------------------------------------------------------------------ compile, every frame
  const preview = useMemo(() => compilePreview(graph), [graph]);
  const [serverIssues, setServerIssues] = useState<CompileIssue[]>([]);

  const errors: CompileIssue[] = useMemo(
    () => (preview.ok ? serverIssues : [...preview.errors, ...serverIssues]),
    [preview, serverIssues],
  );
  const warnings: CompileIssue[] = preview.warnings;
  const steps: PlaybookStep[] = preview.ok ? preview.steps : lastGoodSteps(detail);

  const issueMap = useMemo(() => issuesByNode(errors), [errors]);
  const warnMap = useMemo(() => issuesByNode(warnings), [warnings]);
  const flowErrors = useMemo(() => flowLevelIssues(errors), [errors]);

  /**
   * A BRANCH OUTLET IS A HANDLE, AND RENAMING ONE MOVES IT.
   *
   * React Flow measures each node's handles once and caches their positions by handle id. A
   * branch outcome renamed from "outage" to "no_internet" is a handle id change with no size
   * change, so nothing invalidates that cache — the edge points at a handle React Flow has
   * never heard of and the wire silently stops being drawn. Measured before this: renaming one
   * outcome took the canvas from ten drawn edges to nine, with the edge still in state and
   * still in the read-through. `updateNodeInternals` is the documented cure and this is
   * exactly the case it is documented for.
   */
  const caseSignature = useMemo(
    () =>
      graph.nodes
        .filter((n) => n.type === 'branch')
        .map((n) => `${n.id}:${casesOf(n).join(',')}`)
        .join('|'),
    [graph.nodes],
  );
  useEffect(() => {
    for (const node of graph.nodes) {
      if (node.type === 'branch') updateNodeInternals(node.id);
    }
    // Keyed on the outlets, not on the graph: re-measuring on every drag frame would be a
    // measurement storm on a large flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSignature]);

  // ------------------------------------------------------------------ selection + focus
  const selectedNode: NdNode | null = graph.nodes.find((n) => n.selected) ?? null;
  const [focusToken, setFocusToken] = useState(0);

  const select = useCallback(
    (nodeId: string | null, alsoFocusInspector = false) => {
      apply((current) => ({
        nodes: current.nodes.map((n) => (n.selected === (n.id === nodeId) ? n : { ...n, selected: n.id === nodeId })),
        edges: current.edges,
      }));
      if (alsoFocusInspector) setFocusToken((t) => t + 1);
    },
    [apply],
  );

  const revealNode = useCallback(
    (nodeId: string) => {
      select(nodeId);
      const node = nodeById(graph, nodeId);
      if (node) void rf.setCenter(node.position.x + 130, node.position.y + 60, { zoom: 1, duration: 190 });
    },
    [graph, rf, select],
  );

  // ------------------------------------------------------------------ canvas interactions
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [hotEdge, setHotEdge] = useState<string | null>(null);

  /**
   * THE THREE COLUMNS END WHERE THE WINDOW ENDS. MEASURED, NOT ASSUMED.
   *
   * flow.css carried `height: calc(100vh - 214px)` with 214 spelled out as "top bar, demo
   * band, title row and the compile banner". It is a real height and it was wrong: measured
   * in a 1000px window the rail ran from y=246 to y=1032, so 32px of it — and of the palette
   * inside it — lived below the fold, and the page grew a scrollbar under a layout whose
   * whole point is that it does not scroll. The number cannot be right for everyone anyway:
   * the demo band is a workspace setting, and the callout above the canvas comes and goes
   * as a flow is drawn, each one moving this element without changing the constant.
   *
   * So it is measured from where the element actually starts. The CSS height stays as the
   * pre-hydration fallback; this overrides it on the first frame and on every resize.
   */
  const measureBody = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const raw = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--nd-page-pad'),
      10,
    );
    const gutter = Number.isFinite(raw) ? raw : 16;
    // Read the top with the inline height cleared, or every measurement after the first is
    // taken against a page this element has already resized.
    el.style.height = '';
    const top = el.getBoundingClientRect().top;
    const height = Math.max(FLOOR, Math.round(window.innerHeight - top - gutter));
    el.style.height = `${height}px`;
    /*
     * ONE CORRECTIVE PASS, because the shell owns the space below this screen and this
     * screen does not know what is in it. Subtracting the page padding got within 16px and
     * the page still scrolled — near enough is not the same as not scrolling. Rather than
     * guess at another constant, ask the document how far it overflowed and give that back.
     * Bounded to one pass and to the height we just set, so it cannot oscillate.
     */
    const over = document.documentElement.scrollHeight - window.innerHeight;
    if (over > 0) el.style.height = `${Math.max(FLOOR, height - over)}px`;
  }, []);

  /**
   * A CALLBACK REF, not an effect on mount.
   *
   * This screen returns a skeleton while the flow loads, so on the first render `.ndf-body`
   * does not exist yet — and an effect that finds a null ref and gives up never runs again,
   * because none of its dependencies changed when the real markup arrived. That is exactly
   * how this was written the first time, and it measured nothing at all.
   */
  const attachBody = useCallback(
    (el: HTMLDivElement | null) => {
      bodyRef.current = el;
      if (!el) return;
      measureBody();
      /*
       * And again after the next frame. The first measurement is taken before the browser
       * has finished laying the header out — measured at 1180px wide, the header wrapped one
       * line LATER than at the moment this ran, so the body was sized against a top of 276
       * and then rendered at 302, ending 26px below the window. A second read costs nothing
       * and is taken against the settled layout.
       */
      requestAnimationFrame(measureBody);
    },
    [measureBody],
  );

  /*
   * The header can also change height without anything mounting or unmounting — a long flow
   * name wraps, a badge appears, the window narrows past a breakpoint. Watching it is safe
   * in a way that watching the parent is not: the header's height does not depend on the
   * height this writes.
   */
  useEffect(() => {
    const head = bodyRef.current?.parentElement?.querySelector('.ndf-head');
    if (!head) return undefined;
    const observer = new ResizeObserver(() => measureBody());
    observer.observe(head);
    return () => observer.disconnect();
  }, [measureBody, staleLocked]);

  useEffect(() => {
    window.addEventListener('resize', measureBody);
    return () => window.removeEventListener('resize', measureBody);
  }, [measureBody]);

  // Re-measured when something ABOVE it appears or disappears: the stale-flow callout, the
  // "nothing here yet" line, the compile banner. Deliberately not a ResizeObserver on the
  // parent — this writes a height that changes the parent's height, and an observer watching
  // that is an observer watching itself.
  useEffect(() => {
    measureBody();
  }, [measureBody, staleLocked, isBlank, flowErrors.length]);

  /**
   * FIT, BUT NEVER BELOW READING SIZE — and when it cannot fit, START AT THE TOP.
   *
   * A flow is a tall narrow column: nine cards 264px wide stacked down 1165px of canvas.
   * Plain `fitView` is therefore always height-bound, and measured on the shipped "Slow, not
   * down" starter in a 744×786 canvas it settled at zoom 0.592 — card text at 7.7px, three
   * quarters of the canvas width empty, and not one word on the card legible. The whole
   * promise of the starter list is "press it and read it"; it was drawing a picture of a
   * flow rather than a flow you can read.
   *
   * So the zoom has a floor. When the floor bites, the flow does not fit — and a fit that
   * centres on the middle of a flow that does not fit cuts BOTH ends off, which is the wrong
   * half to lose: a call is read from the top. Below the floor this frames the first step and
   * leaves the rest to a scroll, with the read-through beside it carrying the whole thing in
   * words for anyone who wants it all at once.
   */
  const frame = useCallback(
    (duration = 190) => {
      const ms = prefersReducedMotion() ? 0 : duration;
      const wrap = wrapRef.current;
      const drawn = rf.getNodes();
      if (!wrap || drawn.length === 0) {
        void rf.fitView({ padding: FIT_PADDING, duration: ms });
        return;
      }
      const box = wrap.getBoundingClientRect();
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const node of drawn) {
        const w = node.measured?.width ?? CARD_W;
        const h = node.measured?.height ?? CARD_H;
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + w);
        maxY = Math.max(maxY, node.position.y + h);
      }
      const spanX = Math.max(maxX - minX, 1);
      const spanY = Math.max(maxY - minY, 1);
      const room = 1 - FIT_PADDING * 2;
      const natural = Math.min((box.width * room) / spanX, (box.height * room) / spanY, 1);
      if (natural >= READABLE_ZOOM) {
        void rf.fitView({ padding: FIT_PADDING, duration: ms });
        return;
      }
      // Clamped: centre horizontally, pin the first step just under the top edge.
      const zoom = READABLE_ZOOM;
      const pad = (box.height * FIT_PADDING) / 2;
      void rf.setViewport(
        {
          zoom,
          x: box.width / 2 - (minX + spanX / 2) * zoom,
          y: pad - minY * zoom,
        },
        { duration: ms },
      );
    },
    [rf],
  );

  const starters = (
    <StarterList
      disabled={readOnly}
      activeCode={starterCode}
      onPick={(t) => {
        /*
         * Redraw, do not merge. Picking a second starter after a first must not leave half
         * of the first one wired into it — the point of the list is that you can try three
         * and keep the one that fits.
         *
         * Safe because NOTHING here is saved until Save: the phone keeps answering with the
         * last version that compiled, the read-through says so, and undo puts back whatever
         * was on the canvas a moment ago.
         */
        /*
         * Tidied on the way in. The starters are authored as one long column — the shape a
         * flow has when you write it down, not the shape it should be read in — and eight
         * steps of that is 1,034px against a 640px canvas, so Fit had to shrink the words to
         * seven pixels to obey it. tidy() wraps the run into balanced columns before it is
         * ever drawn, so the first thing on screen is already readable.
         */
        commit(tidy(fromWire(t.graph as WireGraph)), 'starter');
        setStarterCode(t.code);
        /*
         * Frame it. Without this the new drawing inherits the viewport the old one left
         * behind, so a starter with eight steps rendered wherever the previous pan happened
         * to be — half of it clipped under the inspector, the rest off the top. The whole
         * promise of this list is "press it and read it", and you cannot read what is not
         * on screen.
         *
         * Two frames, not one: the nodes do not exist to measure until React has committed
         * them, and `frame()` reads their MEASURED heights, which React Flow writes on the
         * pass after that.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => frame(220));
        });
      }}
    />
  );

  const onNodesChange = useCallback(
    (changes: NodeChange<NdNode>[]) => {
      const resting = changes.some((c) => c.type === 'position' && c.dragging === false);
      const next = (current: NdGraph): NdGraph => ({
        nodes: applyNodeChanges<NdNode>(changes, current.nodes),
        edges: current.edges,
      });
      if (resting) commit(next, 'drag');
      else apply(next);
    },
    [apply, commit],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const removing = changes.some((c) => c.type === 'remove');
      const next = (current: NdGraph): NdGraph => ({
        nodes: current.nodes,
        edges: applyEdgeChanges(changes, current.edges),
      });
      if (removing) commit(next);
      else apply(next);
    },
    [apply, commit],
  );

  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return;
      commit((current) =>
        connectEdge(current, {
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle ?? null,
        }),
      );
    },
    [commit, readOnly],
  );

  /**
   * The compiler follows one wire out of every outlet, so a second wire off the same outlet
   * is a step that silently never runs. Rather than draw one and warn about it, the canvas
   * replaces the old wire (see connect() in graph.ts) — and refuses the shapes that are
   * simply wrong: into the entry, out of an end, a node onto itself.
   */
  const isValidConnection = useCallback<IsValidConnection>(
    (connection) => {
      if (readOnly) return false;
      if (!connection.source || !connection.target) return false;
      if (connection.source === connection.target) return false;
      const source = nodeById(graph, connection.source);
      const target = nodeById(graph, connection.target);
      if (!source || !target) return false;
      return Boolean(NODE_SPECS[source.type as NdKind]?.hasOut && NODE_SPECS[target.type as NdKind]?.hasIn);
    },
    [graph, readOnly],
  );

  /**
   * Pan a freshly added step into view — but ONLY if it is not already on screen.
   *
   * A step is placed 190px below the one it attaches to, and the canvas does not grow: the
   * third step of the first flow anybody builds lands below the fold. Measured, it was not
   * merely awkward, it was clipped to zero height — invisible, and therefore unclickable and
   * untypeable. A step you cannot see is a step that does not exist.
   *
   * Panning unconditionally would be worse in the other direction: it would yank the canvas
   * out from under somebody who can already see exactly where the step landed. So this moves
   * only when it has to, keeps the zoom the person chose, and honours reduced motion.
   */
  const revealPoint = useCallback(
    (position: { x: number; y: number }) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const box = wrap.getBoundingClientRect();
      // The tallest card is a little over 200px of flow space; assume the worst so a card
      // that is only *nearly* on screen still gets brought in.
      const topLeft = rf.flowToScreenPosition(position);
      const bottomRight = rf.flowToScreenPosition({ x: position.x + CARD_W, y: position.y + CARD_H });
      const onScreen =
        topLeft.x >= box.left &&
        topLeft.y >= box.top &&
        bottomRight.x <= box.right &&
        bottomRight.y <= box.bottom;
      if (onScreen) return;
      void rf.setCenter(position.x + CARD_W / 2, position.y + CARD_H / 2, {
        zoom: rf.getZoom(),
        duration: prefersReducedMotion() ? 0 : 190,
      });
    },
    [rf],
  );

  /**
   * ADDING A STEP FROM THE PALETTE ALWAYS PUTS IT IN THE FLOW.
   *
   * Three cases, and the middle one was missing:
   *
   *   1. The selected step has a free outlet     → wire the new step onto it.
   *   2. The selected step is FULL but leads on  → splice the new step into that wire, which
   *                                                is what dropping it on the wire does.
   *   3. Nothing selected                        → drop it loose in the middle of the view.
   *
   * Case 2 used to fall through to case 3, so pressing a palette button while a step in the
   * middle of a finished flow was selected left a card floating beside the flow, wired to
   * nothing, with no word said about it — measured on the shipped emergency starter: nodes
   * 7 → 8, edges 6 → 6. It then turns up as a compile warning about an unreachable step,
   * which is a strange way to be told you pressed a button.
   *
   * It matters more than it looks: the palette button is the ONLY way to add a step without
   * a mouse. Leaving the splice to drag-and-drop made "wire it in properly" a thing only
   * people who can drag are allowed to do.
   */
  /**
   * ADD A STEP AFTER A GIVEN ANCHOR — the one routine behind both the palette and the script.
   *
   *   1. anchor has a free outlet     → wire the new step onto it.
   *   2. anchor is FULL but leads on  → splice the new step into that wire (A→new→B).
   *   3. no anchor                    → drop it loose in the middle of the view.
   *
   * The palette passes the current selection; the script passes the row you pressed + under.
   * `revealPoint` is a no-op when the canvas is not mounted (script view), so this is safe to
   * call from either place.
   */
  const addKindAt = useCallback(
    (anchor: NdNode | null, kind: NdKind) => {
      if (readOnly) return;
      const position = anchor
        ? { x: anchor.position.x, y: anchor.position.y + 190 }
        : viewportCentre(rf, wrapRef.current);
      // Built from the rendered graph so its id is known out here: the new step becomes the
      // selection, which is what makes the palette (and the script's + button) chain.
      const node = makeNode(graph, kind, position);
      const spec = NODE_SPECS[kind];
      const spliceEdge =
        anchor && !hasFreeOutlet(graph, anchor) && spec.hasIn && spec.hasOut
          ? (outgoing(graph, anchor.id)[0]?.id ?? null)
          : null;

      commit((current) => {
        const next = spliceEdge
          ? insertOnEdge(current, node, spliceEdge)
          : appendAfter(current, node, anchor && hasFreeOutlet(current, anchor) ? anchor.id : null);
        return { ...next, nodes: next.nodes.map((n) => (n.selected === (n.id === node.id) ? n : { ...n, selected: n.id === node.id })) };
      });
      if (spliceEdge) {
        toast.info('Slotted in', 'That step was already leading somewhere, so this went in between the two.');
      }
      revealPoint(position);
    },
    [commit, graph, readOnly, revealPoint, rf, toast],
  );

  const addKind = useCallback((kind: NdKind) => addKindAt(selectedNode, kind), [addKindAt, selectedNode]);

  const addAfter = useCallback(
    (afterId: string, kind: NdKind) => addKindAt(nodeById(graph, afterId) ?? null, kind),
    [addKindAt, graph],
  );

  /** Remove one step and heal the wire through it — the script's − and the inspector's both. */
  const deleteNode = useCallback(
    (nodeId: string) => {
      if (readOnly) return;
      const node = nodeById(graph, nodeId);
      if (!node || node.type === 'entry') return;
      const result = removeNodes(graph, [nodeId]);
      commit(result.graph);
      if (result.dropped > 0) {
        toast.warn(
          'Removed a branch',
          'A branch has several tails and a joined-up wire can only follow one, so the first outcome was kept.',
        );
      }
    },
    [commit, graph, readOnly, toast],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setHotEdge(null);
      if (readOnly) return;
      const raw = event.dataTransfer.getData(DRAG_MIME);
      if (!(raw in NODE_SPECS)) return;
      const kind = raw as NdKind;
      const point = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const position = { x: point.x - 132, y: point.y - 44 };
      const edgeId = edgeUnderPointer(event.clientX, event.clientY);
      const node = makeNode(graph, kind, position);
      commit((current) => {
        const next = edgeId ? insertOnEdge(current, node, edgeId) : { nodes: [...current.nodes, node], edges: current.edges };
        return { ...next, nodes: next.nodes.map((n) => (n.selected === (n.id === node.id) ? n : { ...n, selected: n.id === node.id })) };
      });
      if (edgeId) toast.info('Slotted in', 'The step was placed between the two it landed on.');
    },
    [commit, graph, readOnly, rf, toast],
  );

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (readOnly) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setHotEdge(edgeUnderPointer(event.clientX, event.clientY));
    },
    [readOnly],
  );

  /**
   * THE KEYBOARD ROUTE TO A STEP, WHICH THE INSPECTOR HAS BEEN PROMISING ALL ALONG.
   *
   * The empty inspector says "Click a step on the canvas — or tab to it and press Enter — to
   * change its words." Driven with a keyboard, neither half of that was true:
   *
   *   TAB — the cards are focusable, but they sit after the whole rail in DOM order. From the
   *   top of the screen, twenty-five presses of Tab were still inside the starter list and had
   *   not reached step one. That is not "tab to it", it is an endurance test.
   *
   *   ENTER — nothing listened for it. React Flow focuses a card; the card did nothing.
   *
   * `jumpToSteps` is the skip link every long list of controls should have, and this handler
   * is the second half. Space works too, because a focused thing that responds to Enter and
   * ignores Space is a thing people think is broken.
   */
  const jumpToSteps = useCallback(() => {
    const first = wrapRef.current?.querySelector<HTMLElement>('.react-flow__node');
    if (!first) return;
    first.focus();
    const nodeId = first.getAttribute('data-id');
    if (nodeId) select(nodeId);
  }, [select]);

  const onCanvasKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = (event.target as HTMLElement | null)?.closest?.('.react-flow__node');
      const nodeId = card?.getAttribute('data-id');
      if (!nodeId) return;
      event.preventDefault();
      select(nodeId, true);
    },
    [select],
  );

  const deleteSelection = useCallback(() => {
    if (readOnly) return;
    const doomed = graph.nodes.filter((n) => n.selected && n.type !== 'entry').map((n) => n.id);
    if (doomed.length === 0) return;
    const result = removeNodes(graph, doomed);
    commit(result.graph);
    if (result.dropped > 0) {
      toast.warn(
        'Removed a branch',
        'A branch has several tails and a joined-up wire can only follow one, so the first outcome was kept.',
      );
    }
  }, [commit, graph, readOnly, toast]);

  // ------------------------------------------------------------------ saving
  const dirty = !sameGraph(graph, savedGraph);
  const [saving, setSaving] = useState(false);
  const blocked = !preview.ok;

  const save = useCallback(async () => {
    if (!detail || readOnly || saving) return;
    if (!preview.ok) {
      toast.error(
        'Not saved',
        `There ${preview.errors.length === 1 ? 'is 1 problem' : `are ${preview.errors.length} problems`} to fix first. Each one is marked on the step that caused it.`,
      );
      return;
    }
    setSaving(true);
    setServerIssues([]);
    const outcome = await saveFlowGraph(detail.id, { graph: toWire(graph) as WireGraph });
    setSaving(false);

    if (outcome.ok) {
      const next = fromWire(outcome.flow.graph);
      // The server's copy is the truth about what the phone now runs. Reset to it rather
      // than keeping the local drawing, or the two quietly diverge by one rounded pixel.
      const landed = next.nodes.length > 0 ? next : graph;
      // Selection is not part of the saved shape, so a naive reset drops it — and pressing
      // Save while writing a sentence would empty the inspector out from under you. Carry the
      // selected step across.
      const keep = graph.nodes.find((n) => n.selected)?.id ?? null;
      const restored: NdGraph = keep
        ? { nodes: landed.nodes.map((n) => (n.id === keep ? { ...n, selected: true } : n)), edges: landed.edges }
        : landed;
      history.reset(restored);
      setSavedGraph(restored);
      queryClient.setQueryData(['flow', detail.id], outcome.flow);
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast.success('Saved', 'This is what the assistant says from the next call onwards.');
      return;
    }

    if (outcome.kind === 'compile') {
      setServerIssues(outcome.issues);
      toast.error('Refused', outcome.detail);
      return;
    }
    toast.error('Not saved', outcome.detail);
  }, [detail, graph, history, preview, queryClient, readOnly, saving, toast]);

  // ------------------------------------------------------------------ keyboard
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void save();
        return;
      }
      if (typing) return;

      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) history.redo();
        else history.undo();
        return;
      }
      if (mod && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        history.redo();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (graph.nodes.some((n) => n.selected)) {
          event.preventDefault();
          deleteSelection();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelection, graph.nodes, history, save]);

  // Leaving with an unsaved drawing loses work that took an hour. There is no router-level
  // block available on a BrowserRouter, so this is the browser's own guard, which is the one
  // that also covers a closed tab.
  useEffect(() => {
    if (!dirty) return undefined;
    const guard = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  // ------------------------------------------------------------------ render
  const situationLabel = useCallback(
    (key: string) => situations.find((s) => s.key === key)?.label ?? key,
    [situations],
  );

  const ui = useMemo(
    () => ({
      issues: issueMap,
      warnings: warnMap,
      situationLabel,
      readOnly,
      onEdit: (nodeId: string) => select(nodeId, true),
      trigger: detail?.trigger ? flowTriggerLabel(detail.trigger) : '',
    }),
    [detail?.trigger, issueMap, readOnly, select, situationLabel, warnMap],
  );

  // The editor of whatever is selected. The same panel serves both views — the script's rows
  // and the canvas's cards both just select a node, and this edits it.
  const inspectorEl = (
    <Inspector
      node={selectedNode}
      readOnly={readOnly}
      issues={selectedNode ? (issueMap.get(selectedNode.id) ?? []) : []}
      situations={situations}
      focusToken={focusToken}
      onPatch={(nodeId, patch) =>
        commit((current) => patchData(current, nodeId, patch), `patch:${nodeId}:${Object.keys(patch)[0] ?? ''}`)
      }
      onAddCase={(nodeId, value) => commit((current) => addCase(current, nodeId, value))}
      onRenameCase={(nodeId, from, to) => commit((current) => renameCase(current, nodeId, from, to))}
      onRemoveCase={(nodeId, value) => commit((current) => removeCase(current, nodeId, value))}
      onDelete={(nodeId) => deleteNode(nodeId)}
    />
  );

  const renderEdges = useMemo(
    () =>
      graph.edges.map((edge) => ({
        ...edge,
        ...DEFAULT_EDGE,
        className: [edge.id === hotEdge ? 'ndf-edge-hot' : '', edge.sourceHandle ? 'ndf-edge-case' : '']
          .filter(Boolean)
          .join(' '),
      })),
    [graph.edges, hotEdge],
  );

  const renderNodes = useMemo(
    () =>
      graph.nodes.map((node) => ({
        ...node,
        draggable: !readOnly,
        // Read out by a screen reader when the node takes focus. Without it every card is
        // announced as "group", which is true and useless.
        ariaLabel: describe(node, situationLabel),
      })),
    [graph.nodes, readOnly, situationLabel],
  );

  if (query.isLoading) {
    return (
      <div className="ndf-screen">
        <SkeletonCard lines={6} />
      </div>
    );
  }

  if (query.isError || !detail) {
    return (
      <div className="ndf-screen">
        <ErrorState
          error={query.error ?? new Error('That flow could not be loaded.')}
          action="open this call flow"
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }

  return (
    <FlowUiContext.Provider value={ui}>
      <div className="ndf-screen">
        <Header
          detail={detail}
          dirty={dirty}
          saving={saving}
          blocked={blocked}
          blank={isBlank && !staleLocked}
          locked={staleLocked}
          errorCount={preview.ok ? 0 : preview.errors.length}
          canWrite={canWrite}
          readOnly={readOnly}
          history={history}
          view={view}
          onView={setView}
          onSave={() => void save()}
          onFit={() => frame()}
          onJumpToSteps={jumpToSteps}
          hasSteps={graph.nodes.length > 0}
          onBack={() => navigate('/flows')}
        />

        {/* Only in the diagram: the script shows a template flow itself, read-only, with its
            own unlock. The canvas cannot, so it keeps the banner-and-veil pair. */}
        {staleLocked && view === 'diagram' ? (
          <Callout tone="warn" icon="alert-triangle">
            <b>This flow was not drawn here.</b> Its {detail.stepCount} steps came from somewhere else — a
            shipped template or a hand edit — so there is no drawing to open. Drawing a new one
            REPLACES what the phone says today. What it says today is in the read-through on the
            right; read it first.{' '}
            {canWrite ? (
              <LinkAction onClick={() => setAcknowledgedStale(true)} accent>
                Draw a new flow over it
              </LinkAction>
            ) : null}
          </Callout>
        ) : null}

        {/*
          A BLANK FLOW IS NOT A BROKEN ONE.

          The compiler reports "this flow does not do anything yet" as an error, which is
          correct for the compiler — there is nothing to run. Rendering it in red across the
          top of a flow somebody created four seconds ago is not: it makes the product's
          first impression an alarm about a mistake the operator has not had a chance to
          make. It reads as broken software rather than an empty page.

          So an untouched flow gets the sentence in the ordinary voice and a nudge to the
          list beside it. The moment somebody draws something, real errors are real again.
        */}
        {isBlank && !staleLocked && view === 'diagram' ? (
          <Callout tone="neutral" icon="info">
            Nothing here yet. Pick one of the starters on the left and it appears on the
            canvas, or drag a step across to begin your own.
          </Callout>
        ) : null}

        {flowErrors.length > 0 && !staleLocked && !isBlank ? (
          <Callout tone="danger" icon="alert-triangle">
            {flowErrors.map((message) => (
              <span key={message} className="ndf-flowerror">
                {message}
              </span>
            ))}
          </Callout>
        ) : null}

        {view === 'script' ? (
          <div className="ndf-body ndf-body--script" ref={attachBody}>
            <ScriptView
              graph={graph}
              steps={steps}
              selectedId={selectedNode?.id ?? null}
              readOnly={readOnly}
              stale={staleLocked}
              canWrite={canWrite}
              situationLabel={situationLabel}
              trigger={ui.trigger}
              issues={issueMap}
              onSelect={(nodeId) => select(nodeId, true)}
              onAddAfter={addAfter}
              onDelete={deleteNode}
              onDrawOver={() => setAcknowledgedStale(true)}
            />
            <div className="ndf-side ndf-side--script">{inspectorEl}</div>
          </div>
        ) : (
        /* Drawn once, placed twice — the rail's order changes, the list does not. */
        <div className="ndf-body" ref={attachBody}>
          <div className="ndf-rail">
            {/*
              ORDER FOLLOWS WHAT YOU NEED RIGHT NOW.

              On an empty flow the starters come first, because "pick one and read it" is the
              only useful next move and a list of ten step types is not. Once something is
              drawn, the palette leads — you are adding to a flow rather than choosing one.

              Measured before this: with the palette pinned on top, the starters began 1106px
              down a 758px column, so the one thing a new flow needed was the one thing below
              the fold.
            */}
            {isBlank ? starters : null}

            <Palette
              onAdd={addKind}
              disabled={readOnly}
              // Said for ANY selected step, not only one with a free outlet. A step in the
              // middle of a finished flow is the commonest thing to have selected, and the
              // hint used to go quiet there — on the one press where knowing where the step
              // lands matters most, because it is being slotted into an existing wire.
              attachHint={selectedNode ? `after “${shortWords(selectedNode, situationLabel)}”` : null}
            />

            {isBlank ? null : starters}
          </div>

          <div className="ndf-canvas-wrap" ref={wrapRef} onKeyDown={onCanvasKeyDown}>
            <ReactFlow
              nodes={renderNodes}
              edges={renderEdges}
              nodeTypes={NODE_TYPES}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={() => setHotEdge(null)}
              onPaneClick={() => select(null)}
              onNodeDoubleClick={(_event, node) => select(node.id, true)}
              nodesConnectable={!readOnly}
              nodesDraggable={!readOnly}
              elementsSelectable
              // Delete is handled by this screen so it can heal the wire through the step it
              // removes. React Flow's own delete would orphan everything after it.
              deleteKeyCode={null}
              minZoom={0.25}
              maxZoom={1.6}
              fitView
              // minZoom here is the FIT's floor, not the canvas's — you can still pinch out
              // to 0.25 to see the shape of a big flow. It stops the first paint landing on
              // type nobody can read; see frame() for why that is the wrong default.
              fitViewOptions={{ padding: FIT_PADDING, minZoom: READABLE_ZOOM }}
              // The canvas library's badge names a vendor on a screen a customer looks at,
              // which is the one thing we do not do anywhere else in this product. Removing
              // it is Kevin's call and it is made: the library is MIT and charges nothing for
              // commercial use, and the badge is a request from its maintainers rather than a
              // term of the licence. Revisit with a subscription if we lean on their support.
              proOptions={{ hideAttribution: true }}
              className="ndf-canvas"
            >
              <Background variant={BackgroundVariant.Dots} gap={20} size={1.4} />
              {graph.nodes.length > 8 ? (
                <MiniMap pannable zoomable ariaLabel="A small map of the whole flow" className="ndf-minimap" />
              ) : null}
              <Panel position="bottom-left" className="ndf-zoom">
                <button type="button" onClick={() => void rf.zoomOut()} title="Zoom out">
                  <Icon name="minus" size={19} />
                  <span className="nd-sr">Zoom out</span>
                </button>
                <button type="button" onClick={() => void rf.zoomIn()} title="Zoom in">
                  <Icon name="plus" size={19} />
                  <span className="nd-sr">Zoom in</span>
                </button>
                <button
                  type="button"
                  onClick={() => frame()}
                  title="Fit the whole flow on screen"
                >
                  <Icon name="empty-box" size={19} />
                  <span className="nd-sr">Fit the whole flow on screen</span>
                </button>
              </Panel>
            </ReactFlow>

            {staleLocked ? (
              <div className="ndf-veil">
                <div className="ndf-veil-card">
                  <Icon name="lock" size={18} />
                  <p className="ndf-veil-title">Nothing to draw yet</p>
                  <p className="ndf-veil-body">
                    This flow’s steps were not built on this canvas. Read what it says today, on the
                    right, before you replace it.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="ndf-side">
            {inspectorEl}

            <ReadThrough
              steps={steps}
              errors={errors}
              warnings={warnings}
              live={!dirty && preview.ok}
              blank={isBlank}
              onFocusNode={revealNode}
            />
          </div>
        </div>
        )}
      </div>
    </FlowUiContext.Provider>
  );
}

// ------------------------------------------------------------------------------- the header

type HeaderProps = {
  detail: FlowDetail;
  dirty: boolean;
  saving: boolean;
  blocked: boolean;
  /** Nothing drawn yet. An empty flow is not a broken one, and the button must not say it is. */
  blank: boolean;
  /** The flow's steps were not drawn here, so the canvas is empty but the phone is not. */
  locked: boolean;
  errorCount: number;
  canWrite: boolean;
  readOnly: boolean;
  history: ReturnType<typeof useFlowHistory>;
  view: 'script' | 'diagram';
  onView: (view: 'script' | 'diagram') => void;
  onSave: () => void;
  onFit: () => void;
  /** Focuses the first card on the canvas. See jumpToSteps. */
  onJumpToSteps: () => void;
  hasSteps: boolean;
  onBack: () => void;
};

function Header(props: HeaderProps): ReactElement {
  const { detail, dirty, saving, blocked, blank, locked, errorCount, canWrite, readOnly, history } = props;
  const [renaming, setRenaming] = useState(false);

  return (
    <header className="ndf-head">
      <div className="ndf-head-left">
        {/* Off screen until it is focused, which is the first Tab on this page. Without it
            the first step of the flow is twenty-five Tabs away, behind the whole rail. */}
        {props.hasSteps ? (
          <button type="button" className="ndf-skip" onClick={props.onJumpToSteps}>
            Skip to the steps
          </button>
        ) : null}
        <button type="button" className="ndf-back" onClick={props.onBack}>
          <Icon name="chevron-left" size={17} /> Call flows
        </button>
        <h1 className="ndf-title">{detail.name}</h1>
        <HelpTip helpKey="flows.canvas" />
        <Badge tone={detail.kind === 'posture' ? 'ocean' : 'plain'} size="sm">
          {detail.kind === 'posture' ? 'Posture — held until released' : 'Action — fires once'}
        </Badge>
        {detail.armed ? (
          <Badge tone="ok" size="sm" dot>
            In force now
          </Badge>
        ) : null}
        {detail.pinned ? (
          <Badge tone="accent" size="sm" icon="pin">
            {detail.pinLabel ?? 'Pinned'}
          </Badge>
        ) : null}
      </div>

      <div className="ndf-head-meta">
        <span>{detail.trigger ? flowTriggerLabel(detail.trigger) : 'No trigger set'}</span>
        <span className="nd-vr" />
        <span>
          saved <RelativeTime at={detail.updatedAt} />
        </span>
        {dirty ? (
          <>
            <span className="nd-vr" />
            <span className="ndf-dirty">
              <Icon name="edit" size={11} /> unsaved changes
            </span>
          </>
        ) : null}
      </div>

      <div className="ndf-head-actions">
        {/* THE ONE CHOICE THAT CHANGES THE WHOLE SCREEN: read it as a script, or draw it as a
            diagram. Script leads because that is what most people came to do. */}
        <div className="ndf-viewtoggle" role="group" aria-label="How to view this flow">
          <button
            type="button"
            className={props.view === 'script' ? 'is-on' : ''}
            aria-pressed={props.view === 'script'}
            onClick={() => props.onView('script')}
          >
            <Icon name="menu" size={15} /> Script
          </button>
          <button
            type="button"
            className={props.view === 'diagram' ? 'is-on' : ''}
            aria-pressed={props.view === 'diagram'}
            onClick={() => props.onView('diagram')}
          >
            <Icon name="filter" size={15} /> Diagram
          </button>
        </div>

        <div className="ndf-histbtns" role="group" aria-label="Undo and redo">
          <button type="button" onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)">
            <Icon name="refresh" size={19} />
            <span className="nd-sr">Undo</span>
          </button>
          <button
            type="button"
            onClick={history.redo}
            disabled={!history.canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="ndf-redo"
          >
            <Icon name="refresh" size={19} />
            <span className="nd-sr">Redo</span>
          </button>
        </div>

        {props.view === 'diagram' ? <BigButton onClick={props.onFit}>Fit</BigButton> : null}

        {canWrite ? <BigButton onClick={() => setRenaming(true)}>Settings</BigButton> : null}

        {canWrite ? (
          <BigButton
            kind="go"
            disabled={saving || readOnly || !dirty || blocked}
            onClick={props.onSave}
          >
            {/*
              "1 to fix" on a flow four seconds old is a lie about whose fault it is: the one
              thing to fix is that nothing has been drawn, and nobody has had the chance to
              draw it. The button is disabled either way; this is only what it says while it
              is disabled.
            */}
            {/*
              A LOCKED flow is blank on this canvas and NOT blank on the phone — it has
              steps, they just were not drawn here. It read "Nothing to save yet" over a
              seven-step flow that answers calls tonight, beside a veil saying the opposite.
              Locked is checked first because it is the strongest fact about the screen.
            */}
            {saving
              ? 'Saving…'
              : locked
                ? 'Locked — read it first'
                : blank
                  ? 'Nothing to save yet'
                  : blocked
                    ? `${errorCount} to fix`
                    : dirty
                      ? 'Save — this changes what callers hear'
                      : 'Saved'}
          </BigButton>
        ) : (
          <Badge tone="plain" icon="lock">
            Read-only — your role cannot change call flows
          </Badge>
        )}
      </div>

      <RenameDialog open={renaming} detail={detail} onClose={() => setRenaming(false)} />
    </header>
  );
}

/**
 * Name and trigger, in a dialog rather than inline. They are not part of the drawing — they
 * are how somebody finds the flow in a list at 2 AM — and mixing them into the canvas's
 * unsaved state would mean a rename you cannot save because a branch is unfinished.
 */
function RenameDialog({
  open,
  detail,
  onClose,
}: {
  open: boolean;
  detail: FlowDetail;
  onClose: () => void;
}): ReactElement {
  const [name, setName] = useState(detail.name);
  const [trigger, setTrigger] = useState(detail.trigger);
  /**
   * Kind lives here now. It used to be asked on the New flow dialog — before there was
   * anything to look at, which is the worst possible moment to ask whether a flow you have
   * not drawn yet should hold the line or fire once. With that dialog gone this is the only
   * place it can be changed, so it is not optional here.
   */
  const [kind, setKind] = useState<FlowKind>(detail.kind);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setName(detail.name);
      setTrigger(detail.trigger);
      setKind(detail.kind);
    }
  }, [open, detail.name, detail.trigger, detail.kind]);

  const submit = async (): Promise<void> => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      // The save route compiles the drawing every time, so a rename has to carry the current
      // graph with it. Sending the SAVED graph, not the working one — renaming a flow must
      // never quietly publish half-finished edits from the canvas.
      const next = await api.flows.save(detail.id, {
        name: name.trim(),
        trigger: trigger.trim(),
        kind,
        graph: detail.graph,
      });
      queryClient.setQueryData(['flow', detail.id], next.item);
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      toast.success('Saved');
      onClose();
    } catch {
      toast.error('Not saved', 'Nothing was changed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Flow settings"
      subtitle="What it is called, what starts it, and whether it holds the line. None of this changes the words a caller hears — that is the canvas."
      footer={
        <>
          <BigButton onClick={onClose}>Cancel</BigButton>
          <BigButton kind="go" onClick={() => void submit()} disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </BigButton>
        </>
      }
    >
      <div className="ndf-k-field">
        <label htmlFor="ndf-set-name">Name</label>
        <input
          id="ndf-set-name"
          type="text"
          value={name}
          data-autofocus
          onChange={(event) => setName(event.target.value)}
        />
        <p className="ndf-k-hint">How it reads on the flows list and on a pinned button.</p>
      </div>

      <div className="ndf-k-field">
        <label htmlFor="ndf-set-trigger">What starts it</label>
        <input
          id="ndf-set-trigger"
          type="text"
          value={trigger}
          placeholder="Caller says their internet is out"
          onChange={(event) => setTrigger(event.target.value)}
        />
        <p className="ndf-k-hint">
          In your words. It is shown on the Call arrives card so nobody has to guess.
        </p>
      </div>

      {/*
        KIND IS TWO SWITCHES, NOT A DROPDOWN. It is the one setting here that changes what a
        pinned button DOES, and a <select> hides the option you did not choose behind a
        30px-tall control — on a touch screen it hides it behind a native picker. Two tiles
        state both answers at once and the one that is true inverts to a solid plane.
      */}
      <span className="ndf-k-lbl">Kind</span>
      <div className="ndf-k-row-tight">
        <Switches>
          <SwitchTile
            name="Action"
            detail="Fires once and finishes"
            on={kind === 'action'}
            onPress={() => setKind('action')}
          />
          <SwitchTile
            name="Posture"
            detail="Held until released"
            on={kind === 'posture'}
            onPress={() => setKind('posture')}
          />
        </Switches>
        <p className="ndf-k-hint ndf-k-row-tight">
          {kind === 'posture'
            ? 'Pin it and it becomes a switch: while it is on, this is where the line is, and it releases whatever posture was on before.'
            : 'Runs when its trigger matches, then finishes. Pin it and the button fires it once.'}
        </p>
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------------------- helpers

function lastGoodSteps(detail: FlowDetail | undefined): PlaybookStep[] {
  const steps = detail?.steps;
  return Array.isArray(steps) ? (steps as PlaybookStep[]) : [];
}

/** What a card says, cut to a phrase — for "add this after …" and for a screen reader. */
function shortWords(node: NdNode, situationLabel: (key: string) => string): string {
  const kind = node.type as NdKind;
  const data = node.data;
  const pick = (key: string): string => (typeof data[key] === 'string' ? (data[key] as string) : '');
  const words =
    kind === 'say' || kind === 'ask'
      ? pick('text')
      : kind === 'branch'
        ? pick('on')
        : kind === 'arm'
          ? situationLabel(pick('situation'))
          : kind === 'lookup' || kind === 'do'
            ? (pick('tool') ? toolLabel(pick('tool')) : '')
            : '';
  const value = words.trim() || NODE_SPECS[kind].label;
  return value.length > 46 ? `${value.slice(0, 45)}…` : value;
}

/**
 * What a screen reader says when a card takes focus.
 *
 * `shortWords` falls back to the step's own label when the step has no words yet, so the
 * naive "label: words" announced the entry card as "Call arrives: Call arrives" and an
 * unwritten Say as "Say: Say". Once is a name; twice is a stutter that makes the whole
 * canvas sound broken.
 */
function describe(node: NdNode, situationLabel: (key: string) => string): string {
  const kind = node.type as NdKind;
  const label = NODE_SPECS[kind].label;
  const words = shortWords(node, situationLabel);
  return words === label ? label : `${label}: ${words}`;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** A step card in flow-space. Only used to decide whether a card is fully on screen. */
const CARD_W = 264;
const CARD_H = 210;

/**
 * The shortest the three columns may be, whatever the window says.
 *
 * Not a comfort measure — a refusal to collapse, and a deliberate choice about which way to
 * fail. On a 720px-tall window the demo band and a wrapped header take 302px, leaving 394px
 * for the whole editor. Sized to that, the rail's share of the column left the palette
 * showing its heading and none of its nine steps: measured `clipped to zero`. Sized to this
 * instead, the page grows a scrollbar on a short window and every control is still a wheel
 * away. A scrollbar is an inconvenience; a control that renders at zero height is a control
 * that does not exist. On any ordinary window the measured height is larger than this and
 * nothing scrolls at all.
 */
const FLOOR = 520;

/** Breathing room around a fitted flow, as a fraction of the canvas. */
/*
 * Padding is charged on BOTH sides of BOTH axes, so 0.18 was spending 36% of the canvas on
 * margin and then shrinking the type to fit the drawing into the 64% that was left. A nine
 * step flow came out at 0.78 zoom with the bottom half of the canvas empty — squeezed
 * sideways while the room it needed was directly below it. The drawing is what people came
 * to read; give it the room.
 */
const FIT_PADDING = 0.08;

/**
 * The smallest zoom a fit is allowed to choose.
 *
 * The card body is 13px and the eyebrow is 11px. At 0.78 those land at 10.1px and 8.6px —
 * small, and still words. Below it they stop being words: at the 0.592 a plain fit chose on
 * the shipped nine-step starter, the body was 7.7px and the canvas was a picture of a flow
 * rather than a flow. This is a READING floor, not a zoom limit; minZoom on the canvas is
 * still 0.25 and the wheel still goes there.
 */
const READABLE_ZOOM = 0.78;

function viewportCentre(
  rf: ReturnType<typeof useReactFlow>,
  element: HTMLElement | null,
): { x: number; y: number } {
  if (!element) return { x: 0, y: 0 };
  const box = element.getBoundingClientRect();
  const point = rf.screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 3 });
  return { x: point.x - 132, y: point.y };
}

/**
 * Which wire is under the pointer. React Flow gives each edge a wide invisible interaction
 * path, but a drop is a moving target and landing exactly on a 20px band is a knack nobody
 * should need — so four offsets around the point are sampled as well.
 */
function edgeUnderPointer(x: number, y: number): string | null {
  const offsets: [number, number][] = [
    [0, 0],
    [-14, 0],
    [14, 0],
    [0, -14],
    [0, 14],
  ];
  for (const [dx, dy] of offsets) {
    for (const element of document.elementsFromPoint(x + dx, y + dy)) {
      const group = element.closest?.('.react-flow__edge');
      const edgeId = group?.getAttribute('data-id');
      if (edgeId) return edgeId;
    }
  }
  return null;
}
